import { lazy, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";
import ReactHtmlParser from "react-html-parser";

import { RootState } from "src/store/store";

import { useIdleTimer } from "src/hooks/UseIdleTimer";


import {
  ACH_DYNAMIC_COMPONENTS,
  ACH_ENROLLMENT_STEPS_LABEL, SPINNER_TEXT,
} from "src/constants/achEnrollment";
import { NOTIFICATION_MESSAGES } from "src/constants/errors/ApiErrorCode";
import { pagePath } from "src/app/Routers";
import {TWENTY_FIVE_MINUTES_TIMER} from "src/utils/Constants";

// prettier-ignore
const dynamicComponents = {
    [ACH_DYNAMIC_COMPONENTS.Authentication]: lazy(() => import("./authentication")),
    [ACH_DYNAMIC_COMPONENTS.CompanyInfo]: lazy(() => import("./companyInformation")),
    [ACH_DYNAMIC_COMPONENTS.SelectBuyers]: lazy(() => import("./selectBuyers")),
    [ACH_DYNAMIC_COMPONENTS.PaymentDetails]: lazy(() => import("./paymentDetails")),
};

const useAchEnrollmentViewModel = () => {
  const navigate = useNavigate();

  // -- session management states -- //
  const [showWarning, setShowWarning] = useState(false);
  const [isSessionTimedOut, setIsSessionTimedOut] = useState(true);
  const [isAchIntroduction, setIsAchIntroduction] = useState(false);

  const handleTimeout = () => {
    setIsSessionTimedOut(false);

    localStorage.removeItem("id");
    sessionStorage.removeItem("adfsToken");

    navigate(pagePath.sessionPage);
  };

  const handleWarning = () => {
    setShowWarning(true);
  };

  const handleContinue = () => {
    setShowWarning(false);
    if (isSessionTimedOut) resetTimer();
  };

  const resetTimer = useIdleTimer(
    handleTimeout,
    handleWarning,
    isSessionTimedOut && !isAchIntroduction,
    TWENTY_FIVE_MINUTES_TIMER, //25 minutes

  );

  // -- common states -- //
  const [DynamicComponent, setDynamicComponent] = useState(null);

  const [showSpinner, setShowSpinner] = useState(false);

  const stepStatusArray = useSelector(
    (state: RootState) => state.stepTracker.stepStatusArray,
  );

  const selectedComponent: string = useSelector(
    (state: RootState) => state?.achEnrollmentStep?.activeEnrollmentStep,
  );

  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notificationBannerInfo, setNotificationBannerInfo] = useState({
    message: "",
    type: "success",
  });

  // -- dynamic component states -- //
  const [isAchQuestionsSubmitted, setAchQuestionsSubmitted] =
    useState<boolean>(false);

  const stepHeader: string = ACH_ENROLLMENT_STEPS_LABEL[selectedComponent];
  const stepNames = Object.values(ACH_ENROLLMENT_STEPS_LABEL);
  const activeStep = stepNames.indexOf(
    ACH_ENROLLMENT_STEPS_LABEL[selectedComponent],
  );

  const spinnerInfoText =
    selectedComponent === ACH_DYNAMIC_COMPONENTS.PaymentDetails
      ? SPINNER_TEXT.Submission
      : SPINNER_TEXT.Default;


  const displayNotifications = (
    status: boolean,
    validationStatus: string,
    errorCode: string,
  ) => {
    setShowNotificationBanner(false);
    setShowNotificationBanner(status);

    setNotificationBannerInfo({
      message: ReactHtmlParser(NOTIFICATION_MESSAGES[errorCode]),
      type: validationStatus.toLowerCase() === "success" ? "success" : "error",
    });
  };

  const closeNotificationBanner = () => {
    setShowNotificationBanner(false);
    setNotificationBannerInfo({ message: "", type: "" });
  };

  // Define isEmailAsDefaultLanding
  const isEmailAsDefaultLanding =
    selectedComponent === "AchIntroduction" ||
    location?.pathname === pagePath.achDefaultEmailPage;

  useEffect(() => {
    // @ts-expect-error 'Data type'
    setDynamicComponent(dynamicComponents[selectedComponent]);
  }, [selectedComponent]);

  return {
    selectedComponent,
    stepStatusArray,

    stepHeader,
    stepNames,
    activeStep,

    displayNotifications,
    closeNotificationBanner,

    showNotificationBanner,
    notificationBannerInfo,

    DynamicComponent,

    isAchQuestionsSubmitted,
    setAchQuestionsSubmitted,

    isAchIntroduction,
    setIsAchIntroduction,

    isEmailAsDefaultLanding, 

    
    showWarning,
    isSessionTimedOut,
    handleContinue,
    handleTimeout,

    showSpinner,
    setShowSpinner,
    spinnerInfoText,
  };
};

export default useAchEnrollmentViewModel;
